// ProposalContextProvider.jsx
import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import ProposalContext from './ProposalContext';
import ProposalModel from '../../model/proposal/Proposal.model';
import { useLoading } from '../../utils/LoadingUtil';
import { ConfirmAlert, showAlert } from '../../components';
import {
  getSuppliers,
  createSupplier,
  getQuotes,
  getQuoteById,
  createQuote,
  updateQuote,
  deleteQuote,
  updateQuoteState,
  getQuotePdf,
  getQuoteExcel,
  saveAndPrintQuote,
  getExcelExportToPronto,
  updateAndPrintQuote,
} from '../../services/proposalService';
import validateForm from '../../utils/validateForm';
import QuoteModel from '../../model/proposal/QuoteProduct.model';
import { useNavigate } from 'react-router-dom';
import { handleApiError } from '@/lib/handleApiError';
import SupplierModel from '@/model/proposal/Supplier.model';

const ProposalContextProvider = ({ children }) => {
  const {
    proposal,
    setProposal,
    initialState,
    nullifyAlternateProductInfoInQuotes,
    nullifyAlternateTotal,
  } = ProposalModel();
  const {
    isLoading,
    startLoading,
    stopLoading,
    setloadingText,
    loadingText,
    downloadPercentage,
    setDownloadPercentage,
  } = useLoading();
  const [supplierData, setSupplierData] = useState([]);
  const [error, setError] = useState(null);
  const [productForEdit, setProductForEdit] = useState(
    QuoteModel().initialState,
  );

  const { supplier, setSupplier } = SupplierModel();
  const [isProductSelected, setIsProductSelected] = useState(false); // Track if a product is selected
  const [gpError, setGpError] = useState('');
  const [jaybelPriceError, setJaybelPriceError] = useState('');
  const [jaybelIncGstPriceError, setJaybelIncGstPriceError] = useState('');
  const [supplierPriceError, setSupplierPriceError] = useState('');
  const [supplierIncGstPriceError, setSupplierIncGstPriceError] = useState('');
  const [quantityError, setQuantityError] = useState('');
  const [alternateGpError, setAlternateGpError] = useState('');
  const [alternateJaybelPriceError, setAlternateJaybelPriceError] =
    useState('');
  const [alternateJaybelIncGstPriceError, setAlternateJaybelIncGstPriceError] =
    useState('');
  const [alternateSupplierPriceError, setAlternateSupplierPriceError] =
    useState('');
  const [
    alternateSupplierIncGstPriceError,
    setAlternateSupplierIncGstPriceError,
  ] = useState('');
  const [altQuantityError, setAltQuantityError] = useState('');
  const [alternativeValue, setAlternativeValue] = useState('No');
  const [showAlternateProductSection, setShowAlternateProductSection] =
    useState(false);
  const [proposalData, setProposalData] = useState([]);

  const [editProdIndex, setEditProdIndex] = useState(null);
  const navigateTo = useNavigate();
  const [isForUpadte, setIsForUpdate] = useState(false);
  const [copyProposal, setCopyProposal] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [stateFilter, setStateFilter] = useState([]);
  const typeaheadRef = useRef(null); // To access Typeahead instance and clear input
  const [isYesSelected, setIsYesSelected] = useState(true);
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    if (downloadPercentage === 100) {
      setloadingText(''); // Reset after download
      setDownloadPercentage(0); // Reset after download
    }
  }, [downloadPercentage]);

  useEffect(() => {
    // Reset pagination and filters when navigating
    setCurrentPage(0);
    setSearchQuery('');
    setPageSize(10);
    setStateFilter([]);
    setSortBy('');
    setSortDir('desc');
  }, [navigateTo]);

  const validateFields = () => {
    // Validate product fields
    let isValid = true;

    if (
      !productForEdit.mainProductGpRequired &&
      productForEdit.mainProductGpRequired !== 0
    ) {
      setGpError('GP is required');
      isValid = false;
    } else {
      setGpError('');
    }

    // Main Product Jaybel Price (Exc GST)
    if (!productForEdit.mainProductJaybelExcGstPrice) {
      setJaybelPriceError('Jaybel Price is required');
      isValid = false;
    } else {
      setJaybelPriceError('');
    }

    // Main Product Jaybel Price (Inc GST)
    if (
      !productForEdit.mainProductJaybelIncGstPrice &&
      productForEdit.mainProductJaybelIncGstPrice !== 0
    ) {
      setJaybelIncGstPriceError('Jaybel Price Inc GST is required');
      isValid = false;
    } else {
      setJaybelIncGstPriceError('');
    }

    // Main Product Current Supplier Price (Exc GST)
    if (!productForEdit.mainProductCurrentSupplierExcGstPrice) {
      setSupplierPriceError('Current Supplier Price is required');
      isValid = false;
    } else {
      setSupplierPriceError('');
    }

    setSupplierPriceError('');

    //Main Product Current Supplier Price (Exc GST)
    if (!productForEdit.mainProductCurrentSupplierExcGstPrice) {
      setSupplierPriceError('Current Supplier Price Exc GST is required');
      isValid = false;
    } else {
      setSupplierPriceError('');
    }

    // Main Product Current Supplier Price (Inc GST)
    if (
      !productForEdit.mainProductCurrentSupplierIncGstPrice &&
      productForEdit.mainProductCurrentSupplierIncGstPrice !== 0
    ) {
      setSupplierIncGstPriceError('Current Supplier Price Inc GST is required');
      isValid = false;
    } else {
      setSupplierIncGstPriceError('');
    }

    // Main Product Quantity
    if (productForEdit.mainProductQuantity < 0) {
      setQuantityError('Quantity must be zero or greater');
      isValid = false;
    } else {
      setQuantityError('');
    }

    // Alternate Product Validation
    if (alternativeValue === 'Yes') {
      // Alternate Product Quantity
      if (productForEdit.alternateProductQuantity < 0) {
        setAltQuantityError('Quantity must be zero or greater');
        isValid = false;
      } else {
        setAltQuantityError('');
      }

      // Alternate Product GP Required
      if (
        !productForEdit.alternateProductGpRequired &&
        productForEdit.alternateProductGpRequired !== 0
      ) {
        setAlternateGpError('Alternate GP is required');
        isValid = false;
      } else {
        setAlternateGpError('');
      }

      // Alternate Product Jaybel Price (Exc GST)
      if (
        !productForEdit.alternateProductJaybelExcGstPrice &&
        productForEdit.alternateProductJaybelExcGstPrice !== 0
      ) {
        setAlternateJaybelPriceError(
          'Alternate Jaybel Price Exc GST is required',
        );
        isValid = false;
      } else {
        setAlternateJaybelPriceError('');
      }

      // Alternate Product Current Supplier Price (Exc GST)
      if (!productForEdit.alternateProductCurrentSupplierExcGstPrice) {
        setAlternateSupplierPriceError(
          'Alternate Current Supplier Price Exc GST is required',
        );
        isValid = false;
      } else {
        setAlternateSupplierPriceError('');
      }

      // Alternate Product Jaybel Price (Inc GST)
      if (
        !productForEdit.alternateProductJaybelIncGstPrice &&
        productForEdit.alternateProductJaybelIncGstPrice !== 0
      ) {
        setAlternateJaybelIncGstPriceError(
          'Alternate Jaybel Price Inc GST is required',
        );
        isValid = false;
      } else {
        setAlternateJaybelIncGstPriceError('');
      }

      // Alternate Product Current Supplier Price (Inc GST)
      if (
        !productForEdit.alternateProductCurrentSupplierIncGstPrice &&
        productForEdit.alternateProductCurrentSupplierIncGstPrice !== 0
      ) {
        setAlternateSupplierIncGstPriceError(
          'Alternate Current Supplier Price Inc GST is required',
        );
        isValid = false;
      } else {
        setAlternateSupplierIncGstPriceError('');
      }
    }

    return isValid;
  };

  const fetchSuppliers = useCallback(async () => {
    // Fetch supplier data
    try {
      const data = await getSuppliers();
      setSupplierData(data);
    } catch (error) {
      handleApiError(
        'error',
        error.response?.data || 'An error occurred while fetching suppliers',
        error?.response?.status,
      );
    }
  });

  const isValidData = useCallback((proposal) => {
    // Validate proposal data
    // Define error messages for basic fields
    const errorMessages = {
      customer: 'Customer is required',
      quoteAttn: 'Proposal Attn is required',
      currentSupplier: 'Current Supplier Name is required',
      salesPerson: 'Sales Person is required',
      createdDate: 'Created Date is required and cannot be in the future',
      closeDate: 'Exp Close Date is required and cannot be in the past',
    };

    // Perform basic validation and accumulate errors
    const validationErrors = {};
    const isValid = validateForm(
      proposal,
      (errors) => {
        Object.assign(validationErrors, errors);
      },
      errorMessages,
    );

    // Additional checks for nested fields
    if (!proposal.customer?.id) {
      validationErrors.customer = errorMessages.customer;
    }
    if (!proposal.currentSupplier?.id) {
      validationErrors.currentSupplier = errorMessages.currentSupplier;
    }
    if (!proposal.salesPerson?.id) {
      validationErrors.salesPerson = errorMessages.salesPerson;
    }

    // Check date constraints
    const currentDate = new Date().toISOString().split('T')[0];
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const formattedTomorrow = tomorrow.toISOString().split('T')[0];

    const closeDate = proposal.closeDate
      ? new Date(proposal.closeDate.split('-').reverse().join('-'))
          .toISOString()
          .split('T')[0]
      : null;

    const createdDate = proposal.createdDate
      ? new Date(proposal.createdDate.split('-').reverse().join('-'))
          .toISOString()
          .split('T')[0]
      : null;

    // console.log('Current Date:', currentDate);
    // console.log('Close Date:', closeDate);
    // console.log('Created Date:', createdDate);
    // console.log(tomorrow);
    if (createdDate && createdDate > currentDate) {
      // console.log('Create Date Error', createdDate, currentDate);
      validationErrors.createdDate = errorMessages.createdDate;
    } else {
      // console.log('Created Date is valid');
    }

    // Set errors and stop loading if any errors exist
    if (Object.keys(validationErrors).length > 0) {
      setError(validationErrors);
      return false;
    }

    return true;
  }, []);

  const CreateProposal = useCallback(
    async (proposal) => {
      try {
        // Log any existing error state for debugging
        // console.log('1', proposal);

        // Format date fields using a helper function
        const formatDate = (date) => {
          if (!date) return null;
          const [dd, mm, yyyy] = date.split('-');
          if (!dd || !mm || !yyyy) return null;
          return `${dd}-${mm}-${yyyy} 00:00:00`;
        };
        // console.log('2', proposal);
        //console.log('Updated Proposal', proposal);
        let updatedProposal = { ...proposal };
        // console.log('3', updatedProposal);

        updatedProposal.createdDate = formatDate(proposal.createdDate);
        updatedProposal.closeDate = formatDate(proposal.closeDate);
        updatedProposal.updatedDate = proposal.updatedDate
          ? formatDate(proposal.updatedDate)
          : null;

        // console.log('3', updatedProposal);
        if (isYesSelected === false && proposal.state === 'PIPELINE') {
          updatedProposal.quoteProducts = nullifyAlternateProductInfoInQuotes(
            proposal.quoteProducts,
          );
          updatedProposal = nullifyAlternateTotal(updatedProposal);
        }
        // console.log('4', updatedProposal);

        // Reset fields if state is "PENDING"
        if (proposal.state === 'PENDING') {
          updatedProposal = {
            ...updatedProposal,
            id: proposal.id,
            state: proposal.state,
            createdDate: updatedProposal.createdDate,
            closeDate: updatedProposal.closeDate,
            updatedDate: updatedProposal.updatedDate,
            standardTotal: {},
            alternateTotal: {},
            quoteProducts: [],
          };
        }

        // console.log('Updated Proposal:', updatedProposal);
        // Create a new proposal via API
        const res = await createQuote(updatedProposal);

        // Update state and show success alerts based on response
        setProposal(res);
        setCopyProposal(res);

        if (res.state === 'PIPELINE') {
          showAlert('success', 'Proposal Saved successfully');
          navigateTo('/'); // Navigate to the desired page
          setProposal(initialState); // Reset proposal state
          setCopyProposal(initialState);
        } else {
          setError({});
        }
      } catch (error) {
        // Handle errors and display an alert
        handleApiError(
          'error',
          error.response?.data ||
            'An error occurred while creating the proposal',
          error?.response?.status,
        );
      }
    },
    [isValidData, error, isYesSelected],
  );

  const updateProposal = useCallback(
    async (proposal) => {
      // Start loading spinner

      try {
        let updatedProposal = { ...proposal };

        // Handle quote products if not isYesSelected
        // console.log('Is Yes Selected:', isYesSelected);
        if (isYesSelected == false) {
          updatedProposal.quoteProducts = nullifyAlternateProductInfoInQuotes(
            proposal.quoteProducts,
          );
          updatedProposal = nullifyAlternateTotal(updatedProposal);
        }
        // console.log(updatedProposal);

        // Format date fields
        const formatDate = (date) => {
          if (!date) return null;
          const [dd, mm, yyyy] = date.split('-');
          if (!dd || !mm || !yyyy) return null;
          return `${dd}-${mm}-${yyyy} 00:00:00`;
        };

        updatedProposal.createdDate = formatDate(updatedProposal.createdDate);
        updatedProposal.closeDate = formatDate(updatedProposal.closeDate);
        updatedProposal.updatedDate = formatDate(updatedProposal.updatedDate);

        // Update the proposal via API
        const response = await updateQuote(updatedProposal.id, updatedProposal);

        // Set the updated proposal and its copy
        setCopyProposal(response);
        setProposal(response);

        // Adjust state based on the response
        if (
          response.state === 'PIPELINE' ||
          response.state === 'WON' ||
          response.state === 'LOST'
        ) {
          setIsForUpdate(true);
        }
      } catch (error) {
        handleApiError('error', error.response?.data, error?.response?.status);
        throw error;
      }
    },
    [isYesSelected],
  );

  const handleSubmit = async (proposal) => {
    startLoading();
    try {
      if (
        proposal.state === 'PIPELINE' ||
        proposal.state === 'WON' ||
        proposal.state === 'LOST' ||
        proposal.state === 'SAVED'
      ) {
        await updateProposal(proposal);
      } else if (proposal.state === 'PENDING') {
        await CreateProposal(proposal);
      }
    } catch (error) {
      return error;
    } finally {
      stopLoading();
    }
  };

  const handleSubmitWithoutLoader = async (proposal) => {
    try {
      if (
        proposal.state === 'PIPELINE' ||
        proposal.state === 'WON' ||
        proposal.state === 'LOST' ||
        proposal.state === 'SAVED'
      ) {
        await updateProposal(proposal);
      } else if (proposal.state === 'PENDING') {
        // console.log('Proposal', proposal);
        await CreateProposal(proposal);
      }
    } catch (error) {
      return error;
    }
  };

  const handleSaveAndPrint = async (proposal) => {
    // Handle save and print
    setloadingText(`Preparing PDF for download...`);
    startLoading();
    let updatedProposal = { ...proposal };

    if (
      proposal.state === 'PIPELINE' ||
      proposal.state === 'WON' ||
      proposal.state === 'LOST' ||
      proposal.state === 'SAVED'
    ) {
      updatedProposal = { ...proposal };
    } else {
      updatedProposal = { ...proposal, state: 'PIPELINE' };
    }

    if (isYesSelected == false) {
      updatedProposal.quoteProducts = nullifyAlternateProductInfoInQuotes(
        proposal.quoteProducts,
      );
      updatedProposal = nullifyAlternateTotal(updatedProposal);
    }

    if (updatedProposal.createdDate) {
      const [dd, mm, yyyy] = updatedProposal.createdDate.split('-');
      if (!dd || !mm || !yyyy) return; // Ensure all parts are present
      updatedProposal.createdDate = `${dd}-${mm}-${yyyy} 00:00:00`; // Convert to dd-MM-yyyy 00:00:00
    }
    if (updatedProposal.closeDate) {
      const [dd, mm, yyyy] = updatedProposal.closeDate.split('-');
      if (!dd || !mm || !yyyy) return; // Ensure all parts are present
      updatedProposal.closeDate = `${dd}-${mm}-${yyyy} 00:00:00`; // Convert to dd-MM-yyyy 00:00:00
    }
    if (updatedProposal.updatedDate) {
      const [dd, mm, yyyy] = updatedProposal.updatedDate.split('-');
      if (!dd || !mm || !yyyy); // Ensure all parts are present
      updatedProposal.updatedDate = `${dd}-${mm}-${yyyy} 00:00:00`; // Convert to dd-MM-yyyy 00:00:00
    } else {
      updatedProposal.updatedProposal = null;
    }
    try {
      const response = await saveAndPrintQuote(updatedProposal, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          const total = progressEvent.total || 0;
          const current = progressEvent.loaded || 0;
          const percentage = Math.floor((current / total) * 100);
          setDownloadPercentage(percentage); // Update UI
          setloadingText(`Downloading PDF: ${percentage}%`);
          // console.log(`Download Progress: ${percentage}%`);
        },
      });
      const url = window.URL.createObjectURL(response);
      const newTab = window.open();
      if (newTab) {
        newTab.location.href = url;
        showAlert('success', 'Proposal saved  successfully');
      } else {
        handleApiError(
          'error',
          'Popup blocked. Please allow popups for this site.',
          error?.response?.status,
        );
      }
    } catch (error) {
      handleApiError('error', error.response?.data, error?.response?.status);
    } finally {
      stopLoading();
      setloadingText('');
      setDownloadPercentage(0);
    }
  };
  const handleUpdateAndPrint = async (proposal) => {
    // Handle save and print
    setloadingText(`Preparing PDF for download...`);
    startLoading();
    let updatedProposal = { ...proposal };

    if (
      proposal.state === 'PIPELINE' ||
      proposal.state === 'WON' ||
      proposal.state === 'LOST'
    ) {
      updatedProposal = { ...proposal };
    } else {
      updatedProposal = { ...proposal, state: 'PIPELINE' };
    }

    if (isYesSelected == false) {
      updatedProposal.quoteProducts = nullifyAlternateProductInfoInQuotes(
        proposal.quoteProducts,
      );
      updatedProposal = nullifyAlternateTotal(updatedProposal);
    }

    if (updatedProposal.createdDate) {
      const [dd, mm, yyyy] = updatedProposal.createdDate.split('-');
      if (!dd || !mm || !yyyy) return; // Ensure all parts are present
      updatedProposal.createdDate = `${dd}-${mm}-${yyyy} 00:00:00`; // Convert to dd-MM-yyyy 00:00:00
    }
    if (updatedProposal.closeDate) {
      const [dd, mm, yyyy] = updatedProposal.closeDate.split('-');
      if (!dd || !mm || !yyyy) return; // Ensure all parts are present
      updatedProposal.closeDate = `${dd}-${mm}-${yyyy} 00:00:00`; // Convert to dd-MM-yyyy 00:00:00
    }
    if (updatedProposal.updatedDate) {
      const [dd, mm, yyyy] = updatedProposal.updatedDate.split('-');
      if (!dd || !mm || !yyyy); // Ensure all parts are present
      updatedProposal.updatedDate = `${dd}-${mm}-${yyyy} 00:00:00`; // Convert to dd-MM-yyyy 00:00:00
    } else {
      updatedProposal.updatedProposal = null;
    }
    try {
      const response = await updateAndPrintQuote(updatedProposal, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          const total = progressEvent.total || 0;
          const current = progressEvent.loaded || 0;
          const percentage = Math.floor((current / total) * 100);
          setDownloadPercentage(percentage); // Update UI
          setloadingText(`Downloading PDF: ${percentage}%`);
          // console.log(`Download Progress: ${percentage}%`);
        },
      });
      const url = window.URL.createObjectURL(response);
      const newTab = window.open();
      if (newTab) {
        newTab.location.href = url;
        showAlert('success', 'Proposal saved  successfully');
      } else {
        handleApiError(
          'error',
          'Popup blocked. Please allow popups for this site.',
          error?.response?.status,
        );
      }
    } catch (error) {
      handleApiError('error', error.response?.data, error?.response?.status);
    } finally {
      stopLoading();
      setloadingText('');
      setDownloadPercentage(0);
    }
  };

  const updateStates = useCallback(async (updateStateObj) => {
    // Update proposal states
    startLoading();
    try {
      await updateQuoteState(updateStateObj);
      showAlert('success', 'State updated successfully');
      await getQuote();
    } catch (error) {
      handleApiError('error', error.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  });

  const removeQuote = useCallback(async (id) => {
    // Remove a quote
    const result = await ConfirmAlert({
      confirmBtnText: 'Quote deletion Product',
    });
    if (!result.isConfirmed) {
      showAlert('error', 'Quote deletion cancelled');
      stopLoading();
      return;
    }
    startLoading();
    try {
      await deleteQuote(id);
      showAlert('success', 'Quote deleted successfully');
      getQuote();
    } catch (error) {
      handleApiError('error', error.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  }, []);

  const createNewSupplier = useCallback(
    async (supplier) => {
      // Create a new supplier
      // e.preventDefault();
      startLoading();
      try {
        await createSupplier(supplier);
        closeModal();
      } catch (error) {
        setError(error.response?.data || 'An error occurred');
      } finally {
        stopLoading();
        await fetchSuppliers();
      }
    },
    [fetchSuppliers, startLoading, stopLoading],
  );

  const getQuote = useCallback(
    async (
      page = currentPage,
      size = pageSize,
      search = searchQuery,
      states = stateFilter,
      sortField = sortBy, // Renamed parameter to avoid shadowing
      sortDirection = sortDir, // Renamed parameter to avoid shadowing
    ) => {
      // Fetch quotes with pagination and filters

      startLoading();
      try {
        const data = await getQuotes(
          page,
          size,
          search,
          states,
          sortField,
          sortDirection,
        );
        setProposalData(
          data?._embedded?.quoteResponseList || ['No Data Found'],
        );
        setTotalElements(data?.page?.totalElements);
      } catch (error) {
        handleApiError(
          'error',
          error.response?.data || 'An error occurred while fetching quote',
          error?.response?.status,
        );
        setProposalData([]);
        setTotalElements(0);
      } finally {
        stopLoading();
      }
    },
    [
      currentPage,
      pageSize,
      searchQuery,
      stateFilter,
      startLoading,
      stopLoading,
    ],
  );

  const getPropsalById = useCallback(async (id) => {
    // Fetch proposal by ID
    startLoading();
    try {
      const data = await getQuoteById(id);
      setProposal(data);
      setCopyProposal(data);

      if (
        data.state === 'PIPELINE' ||
        data.state === 'WON' ||
        data.state === 'LOST'
      ) {
        setIsForUpdate(true);
      }
    } catch (error) {
      handleApiError(
        'error',
        error.response?.data || 'An error occurred while fetching quote',
        error?.response?.status,
      );
    } finally {
      stopLoading();
    }
  }, []);

  const getproposalPdf = useCallback(async (id) => {
    setloadingText(`Preparing PDF for download...`);
    startLoading();
    try {
      const response = await getQuotePdf(id, {
        responseType: 'blob', // Ensure the response is a blob
        onDownloadProgress: (progressEvent) => {
          const total = progressEvent.total || 0;
          const current = progressEvent.loaded || 0;
          const percentage = Math.floor((current / total) * 100);
          setDownloadPercentage(percentage); // Update UI
          setloadingText(`Downloading PDF: ${percentage}%`);
          // console.log(`Download Progress: ${percentage}%`);
        },
      });
      const url = window.URL.createObjectURL(response);
      const newTab = window.open();
      if (newTab) {
        newTab.location.href = url;
        showAlert('success', 'Proposal PDF opened in new tab successfully');
      } else {
        handleApiError(
          'error',
          'Popup blocked. Please allow popups for this site.',
          error?.response?.status,
        );
      }
      setloadingText(''); // Reset after download
    } catch (error) {
      handleApiError(
        'error',
        error.message || 'An error occurred while fetching the PDF',
        error?.response?.status,
      );
    } finally {
      stopLoading();
      setloadingText('');
      setDownloadPercentage(0);
    }
  }, []);

  const getExcelById = useCallback(async (id) => {
    setloadingText('Preparing Excel for download...');
    startLoading();
    try {
      const response = await getQuoteExcel(id, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          const total = progressEvent.total || 0;
          const current = progressEvent.loaded || 0;
          const percentage = Math.floor((current / total) * 100);
          setDownloadPercentage(percentage);
          setloadingText(`Downloading Excel: ${percentage}%`);
        },
      });
      if (!response || response.size === 0) {
        throw new Error('The Excel file is empty or invalid.');
      }
      const url = window.URL.createObjectURL(response);
      // Check the URL before opening the tab
      // console.log(url);
      const a = document.createElement('a');
      a.href = url;
      a.download = `proposal_${id}.xlsx`; // Name your file
      document.body.appendChild(a);
      a.click(); // Trigger download
      a.remove();
      showAlert('success', 'Proposal Excel opened successfully');
      setloadingText(''); // Reset after download
    } catch (error) {
      handleApiError(
        'error',
        error.message || 'An error occurred while fetching the Excel',
        error?.response?.status,
      );
    } finally {
      stopLoading();
      setloadingText('');
      setDownloadPercentage(0);
    }
  }, []);

  const exportToPronto = useCallback(async (id) => {
    setloadingText('Preparing Excel for download...');
    startLoading();
    try {
      const response = await getExcelExportToPronto(id, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          const total = progressEvent.total || 0;
          const current = progressEvent.loaded || 0;
          const percentage = Math.floor((current / total) * 100);
          setDownloadPercentage(percentage);
          setloadingText(`Downloading Excel: ${percentage}%`);
        },
      });
      if (!response || response.size === 0) {
        throw new Error('The Excel file is empty or invalid.');
      }
      const url = window.URL.createObjectURL(response);
      // Check the URL before opening the tab
      // console.log(url);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Pronto-excel${id}.csv`; // Name your file
      document.body.appendChild(a);
      a.click(); // Trigger download
      a.remove();
      showAlert('success', 'Pronto Excel opened successfully');
      setloadingText(''); // Reset after download
    } catch (error) {
      handleApiError(
        'error',
        error.message || 'An error occurred while fetching the Excel',
        error?.response?.status,
      );
    } finally {
      stopLoading();
      setloadingText('');
      setDownloadPercentage(0);
    }
  }, []);

  const clearErrors = () => {
    setGpError('');
    setJaybelPriceError('');
    setJaybelIncGstPriceError('');
    setSupplierPriceError('');
    setSupplierIncGstPriceError('');
    setQuantityError('');
    setAlternateGpError('');
    setAlternateJaybelPriceError('');
    setAlternateJaybelIncGstPriceError('');
    setAlternateSupplierPriceError('');
    setAlternateSupplierIncGstPriceError('');
    setAltQuantityError('');
  };

  const clearFormData = useCallback(() => {
    // Clear form data
    setError(null);
    setProposal(initialState);
    setIsForUpdate(false);
    setAlternativeValue('No');
    setShowAlternateProductSection(false);
  }, [initialState, setProposal]);

  return (
    <ProposalContext.Provider
      value={{
        getSuppliers: fetchSuppliers,
        initialState,
        supplierData,
        proposal,
        setProposal,
        CreateProposal,
        isValidData,
        error,
        setError,
        createNewSupplier,
        productForEdit,
        setProductForEdit,
        isProductSelected,
        setIsProductSelected,
        validateFields,
        gpError,
        setGpError,
        jaybelPriceError,
        setJaybelPriceError,
        jaybelIncGstPriceError,
        setJaybelIncGstPriceError,
        supplierPriceError,
        setSupplierPriceError,
        supplierIncGstPriceError,
        setSupplierIncGstPriceError,
        quantityError,
        setQuantityError,
        alternateGpError,
        setAlternateGpError,
        alternateJaybelPriceError,
        setAlternateJaybelPriceError,
        alternateJaybelIncGstPriceError,
        setAlternateJaybelIncGstPriceError,
        alternateSupplierPriceError,
        setAlternateSupplierPriceError,
        alternateSupplierIncGstPriceError,
        setAlternateSupplierIncGstPriceError,
        alternativeValue,
        showAlternateProductSection,
        setShowAlternateProductSection,
        setAlternativeValue,
        editProdIndex,
        setEditProdIndex,
        getQuote,
        proposalData,
        setProposalData,
        getPropsalById,
        isForUpadte,
        setIsForUpdate,
        handleSubmit,
        updateProposal,
        deleteQuote: removeQuote,
        clearFormData,
        typeaheadRef,
        totalElements,
        setTotalElements,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        stateFilter,
        setStateFilter,
        searchQuery,
        setSearchQuery,
        updateStates,
        getproposalPdf,
        isLoading,
        startLoading,
        stopLoading,
        loadingText,
        downloadPercentage,
        setDownloadPercentage,
        getExcelById,
        handleSaveAndPrint,
        altQuantityError,
        setAltQuantityError,
        clearErrors,
        exportToPronto,
        isYesSelected,
        setIsYesSelected,
        handleUpdateAndPrint,
        copyProposal,
        handleSubmitWithoutLoader,
        sortBy,
        setSortBy,
        sortDir,
        setSortDir,
        supplier,
        setSupplier,
      }}>
      {children}
    </ProposalContext.Provider>
  );
};

export default ProposalContextProvider;
export const useProposal = () => useContext(ProposalContext);
